import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';

const OutsourcedAccounting = () => {
  usePageTitle('Outsourced Accounting Services');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Outsourced Accounting Services</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/financing1.png' 
            alt="Outsourced Accounting Services" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Managing accounting and finance functions internally can often divert valuable resources away from 
              core operations — especially for startups, growing SMEs, and even large enterprises that require 
              scalability, cost-efficiency, and compliance without the overhead.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, our Outsourced Accounting Services offer end-to-end solutions that bring accuracy, 
              transparency, and timeliness to your finance processes — all while being seamlessly integrated with 
              your business operations.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              We serve as your extended accounting team, whether for day-to-day operations, monthly reporting, 
              or complex financial consolidation.
            </p>
          </Card>
        </div>

        {/* Why Outsourcing Section */}
        <div className="col-12 mt-5">
          <h2>Why Outsourcing Makes Sense</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Reduces the need for hiring, training, and retaining in-house staff</li>
                <li className="mb-2">Eliminates risk of non-compliance due to missed deadlines or inaccurate filings</li>
                <li className="mb-2">Ensures continuity and consistency, even during employee transitions</li>
                <li className="mb-2">Provides access to qualified professionals and accounting technology</li>
                <li>Scalable for startups, SMEs, and large enterprises</li>
              </ul>
            </div>
            <img 
              src='/images/financing2.png' 
              alt="Accounting Outsourcing Benefits" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Scope of Our Outsourced Accounting Services</h2>
          <p className="text-xl mb-4">
            We tailor our offering based on your industry, business size, and complexity. Whether you need a 
            full-fledged virtual finance department or targeted support, we've got you covered.
          </p>
          <Divider />
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Accounting & Bookkeeping" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Day-to-day recording of sales, purchases, expenses, and journal entries</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of trial balance, general ledger, and reconciliations</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Periodic closing of books and adjustments</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Accurate documentation of transactions in compliance with statutory norms</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of MIS reports and dashboards for decision-making</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Invoice Processing & Accounts Payable/Receivable" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>End-to-end management of incoming and outgoing invoices</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Vendor and customer ledger maintenance</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Follow-up for collections and aging analysis</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Payment processing, approvals, and tracking</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>GST-compliant invoicing and input credit matching</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Payroll Processing" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Monthly salary computation and payslip generation</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>TDS, EPF, ESI, and other statutory deductions and filings</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Reimbursement management, full & final settlements</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Payroll compliance with Shops & Establishment Act, PT, and Gratuity</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Customized payroll structuring based on tax efficiency</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12">
              <Card title="4. Ind AS / IFRS Implementation & Consolidation" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Transition planning from Indian GAAP to Ind AS / IFRS</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of financial statements as per global accounting standards</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Group company consolidation, intercompany elimination, and disclosures</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Mapping and reporting on complex accounting treatments</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support during audits and investor presentations</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/financing3.png' 
              alt="Accounting Solutions" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <h2>Your Trusted Accounting Partner</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we transform accounting from a back-office function into a strategic asset. 
                Our outsourced accounting services provide the expertise, technology, and reliability you need 
                to maintain financial health while focusing on growing your business.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                With our team as your extended finance department, you get enterprise-grade accounting 
                capabilities without the enterprise overhead.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutsourcedAccounting;